declare global {
  interface Window {
    dev: () => boolean;
    hasRemote: () => boolean;
    hasMode: (mode) => boolean;
    mode: string;
    reviewModeEnabled: boolean;
    savedStore: any;
    locationHistory: string | null;
    persistHistory: boolean;
    sectionInView: {
      section: HTMLElement;
      id: string;
      inView: boolean;
      index: number;
    };
    IS_CHAMELEON_MODULE: boolean;
    chameleon: any;
    fonts: any;
    FontFace: any;
    isHosted: boolean;
    source: string;
    enableAnalytics: boolean;
    analyticsUrl: string;
    collectUrl: string;
    projectId: number | string;
    accountId: string;
    versionId: number | string;
  }
  interface Document {
    fonts: any;
  }
}

import './setup';
//
import React, { Fragment as _, FC, PropsWithChildren, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Global, css } from '@emotion/react';
import 'assets/whoopsBrowserIncompatible.png';
import { ErrorBoundary } from 'components/errorHandler';
import ModuleImagesPreloader from 'components/lib/PreLoaders/ModuleImagesPreloader';
import Module from 'components/Module';
import { HostingProvider } from 'hosting/context';
import { Provider } from 'react-redux';
import 'regenerator-runtime/runtime.js';
import { addFont } from 'services/fontService';
import getModuleImages from 'services/getModuleImages';
import getStore from 'services/getStore';
import 'services/scorm/scormService';
import smoothscroll from 'smoothscroll-polyfill';
import 'styles/global';
import 'whatwg-fetch';
import { globalStyles } from 'styles/global';
import { SCORM } from 'pipwerks-scorm-api-wrapper';

if (!('IntersectionObserver' in window)) {
  require('intersection-observer');
}

smoothscroll.polyfill();

window.hasRemote = () => (window as any).getParams().hasRemote;

window.dev = () => {
  try {
    return window.parent.IS_CHAMELEON_EDITOR;
  } catch (err) {
    //
  }

  return ['DEVELOPMENT', 'EDIT'].includes(window?.mode);
};

// disable all @hello-pangea/dnd development warnings
window['__@hello-pangea/dnd-disable-dev-warnings'] = true;

const store = getStore();

try {
  window.hasMode = mode => {
    const windowMode = window.mode || 'DEVELOPMENT';
    return mode === windowMode;
  };

  window.client = parent.client;
} catch (err) {
  // console.warn(err);
}

const loadTheme = store.subscribe(() => {
  const state = store.getState();
  const { backgroundColor = '' } = state.theme;

  const fontFamily =
    state.theme?.typeSettings?.fontFamily || state?.theme?.fontFamily;
  const fontFamilies =
    state.theme?.typeSettings?.fontFamilies || state?.theme?.fontFamilies;

  // console.log('loading fonts :', fontFamily, fontFamilies);

  document.body.style.background = backgroundColor;

  if (!!fontFamilies?.length) {
    fontFamilies.forEach(f => {
      addFont(f);
    });
  }

  // just wanting to smash this until it displays for sure
  if (fontFamily) {
    const checkFontFamilyDisplays = setInterval(() => {
      if (document.body.style.fontFamily.includes(fontFamily)) {
        clearInterval(checkFontFamilyDisplays);
      }

      document.body.style.fontFamily = fontFamily;
    }, 0);
  }

  loadTheme.unsubscribe && loadTheme.unsubscribe();
});

const isHosted = (window as any).getParams().hosted;

const enableAnalytics = (window as any).getParams().enableAnalytics;

const ccURL = (window as any).chameleon.CHAMELEON_URL;

export type IsScorm = {
  isInitialized?: boolean;
  scormUserId?: string;
  scormSessionId?: string;
};

let isScorm: IsScorm = {};

if (!(window as any).hosted) {
  try {
    //
    SCORM.version = (window as any).scormVersion || '1.2';

    if (SCORM.init()) {
      isScorm.isInitialized = true;
    }
  } catch (err) {
    console.log(err);
  }
}

const HostingWrapper = ({ children }) => {
  const projectId = (window as any).getParams().projectId;
  const accountId = (window as any).getParams().accountId;
  const versionId = (window as any).getParams().versionId;
  const source = (window as any).getParams().source;

  const [hasCurrentSession, setHasCurrentSession] = useState(!window?.env);

  return (
    <HostingProvider
      isScorm={isScorm}
      isHosted={isHosted}
      enableAnalytics={enableAnalytics}
      url={ccURL}
      source={source}
      projectId={projectId}
      accountId={accountId}
      versionId={versionId}
      currentSessionInitialized={() => {
        setHasCurrentSession(true);
      }}
    >
      {hasCurrentSession ? children : null}
    </HostingProvider>
  );
};

const moduleImages = getModuleImages();

let hideModulePreloaderOverride = false;

const ModuleWithPreloader = () => {
  const [showModule, setShowModule] = useState(false);

  return (
    <_>
      {showModule && <Module isScorm={isScorm} isHosted={isHosted} />}
      <ModuleImagesPreloader
        images={moduleImages}
        onComplete={() => {
          setShowModule(true);
        }}
      />
    </_>
  );
};

const rootElement = document.getElementById('root');
const root = createRoot(rootElement);

root.render(
  <_>
    <Global styles={css(globalStyles)} />
    <ErrorBoundary>
      <HostingWrapper>
        <Provider store={store}>
          {hideModulePreloaderOverride ? (
            <Module isScorm={isScorm} isHosted={isHosted} />
          ) : !window.dev() && store.getState()?.theme?.useImagePreloader ? (
            <ModuleWithPreloader />
          ) : (
            <Module isScorm={isScorm} isHosted={isHosted} />
          )}

          {/* make the preloader available for editing. but do not do any preloading */}
          {window.dev() && (
            <ModuleImagesPreloader images={[]} onComplete={() => {}} />
          )}
        </Provider>
      </HostingWrapper>
    </ErrorBoundary>
  </_>
);
